import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Inject,
    OnInit,
    Output,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AggregatedLimit, AggregatedLimitType, Limit, LimitResponse, LimitType } from '@bpos/v1/limits';
import { StakeConfig } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';

import { IBetslipMessages } from '../../../base/models/betslip-messages';
import { BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, IBetslipMessagesProviderService } from '../../../base/services/betslip-messages-provider.service';
import { UserStakeLimitsService } from '../services/user-stake-limits.service';
import { IStakeProgressBarContext } from './stake-progress-bar.component';

@Component({
    selector: 'bs-user-stake-limits',
    templateUrl: './user-stake-limits.component.html',
    styleUrls: ['./user-stake-limits.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class UserStakeLimitsComponent implements OnInit {
    @HostBinding('class') className = 'betslip-user-stake-limits';
    warnUserAboutRemainingBalance = false;
    daysRemaining = 0;
    currencySymbol = '';
    stakeProgressBarContext: IStakeProgressBarContext;
    @Output() openMonthlyStakeInfoHelp = new EventEmitter<boolean>();
    monthlyStakeInfoHelp = false;
    wasMonthlyStakeInfoHelpShown = false;
    messages: IBetslipMessages;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messagesProvider: IBetslipMessagesProviderService,
        private stakeConfig: StakeConfig,
        private userStakeLimitsService: UserStakeLimitsService,
        private changeDetector: ChangeDetectorRef,
    ) {
        this.stakeProgressBarContext = {
            start: 0,
            end: 0,
            fillPercentage: 0,
            fillValue: 0,
        };
        this.messages = messagesProvider.getMessages();
    }

    ngOnInit(): void {
        this.getStakeLimitsData();
    }

    @HostListener('document:click')
    clickout(): void {
        if (!this.wasMonthlyStakeInfoHelpShown) {
            this.closeMonthlyStakeInfoHelp();
        }
        this.wasMonthlyStakeInfoHelpShown = false;
    }

    showMonthlyStakeInfoHelp(): void {
        this.monthlyStakeInfoHelp = true;
        this.wasMonthlyStakeInfoHelpShown = true;
        this.openMonthlyStakeInfoHelp.emit(this.monthlyStakeInfoHelp);
    }

    closeMonthlyStakeInfoHelp(): void {
        this.monthlyStakeInfoHelp = false;
        this.openMonthlyStakeInfoHelp.emit(this.monthlyStakeInfoHelp);
    }

    getStakeLimitsData(): void {
        this.userStakeLimitsService
            .getUserStakeLimits()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: LimitResponse) => {
                this.stakeProgressBarContext.start = 0;
                const monthlyStakeLimitInfo = this.findLimitByType(LimitType.MonthlyStake, response.limits);
                const aggregatedMonthlyStakeLimitInfo = this.findLimitByType(AggregatedLimitType.MonthlyStakeSum, response.aggregatedLimits);

                if (!(monthlyStakeLimitInfo?.currency && monthlyStakeLimitInfo?.value && aggregatedMonthlyStakeLimitInfo?.value)) {
                    return;
                }

                this.stakeProgressBarContext.end = monthlyStakeLimitInfo.value;
                this.currencySymbol = monthlyStakeLimitInfo.currency;
                this.stakeProgressBarContext.fillValue = monthlyStakeLimitInfo.value - aggregatedMonthlyStakeLimitInfo.value;
                this.daysRemaining = DateProviderService.getDaysTo(DateProviderService.getEndOfMonth());
                const remainingStakePercentage = (this.stakeProgressBarContext.fillValue / this.stakeProgressBarContext.end) * 100;
                this.warnUserAboutRemainingBalance = remainingStakePercentage <= this.stakeConfig.minStakeThresholdValue;
                this.stakeProgressBarContext.fillPercentage = remainingStakePercentage;
                this.changeDetector.markForCheck();
            });
    }

    private findLimitByType<T extends Limit | AggregatedLimit>(type: string, limits: T[]): T | undefined {
        return limits.find((l) => l.type.toLowerCase() === type.toLowerCase());
    }
}
